import { useEffect, useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import { Reveal, Overline, MaskedLine } from "@/components/Reveal";
import { API, trackEvent, formatApiError, QUIZ_URL } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Contact — Wired 2 Win Golf";
    trackEvent("page_view", { page: "contact" });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("done");
      trackEvent("contact_submitted");
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main data-testid="contact-page" className="pt-32 lg:pt-40">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Overline className="mb-6"><MaskedLine>Contact</MaskedLine></Overline>
          <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-6xl uppercase">
            <MaskedLine delay={0.15}>Talk to</MaskedLine>
            <MaskedLine delay={0.3}><span className="text-volt">the team.</span></MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 text-white/60 leading-relaxed max-w-md">
              Coaching inquiries, partnerships, press, or just a question about your assessment — we read everything.
            </p>
            <p className="mt-6 text-sm text-white/40">
              Prefer email? <a href="mailto:dbisbee@egolfgroup.com" className="text-white/70 hover:text-volt transition-colors">dbisbee@egolfgroup.com</a>
            </p>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.2}>
          {status === "done" ? (
            <div data-testid="contact-success" className="border border-volt/40 bg-volt/5 p-10 flex flex-col items-start gap-4">
              <span className="w-12 h-12 bg-volt flex items-center justify-center"><Check className="w-6 h-6 text-obsidian" /></span>
              <h2 className="font-display font-bold text-3xl tracking-tight">Message received.</h2>
              <p className="text-white/60 text-sm leading-relaxed">We'll get back to you within a couple of days. Meanwhile, the assessment is always open.</p>
              <a href={QUIZ_URL} className="text-volt font-bold text-sm hover:text-white transition-colors" data-testid="contact-success-quiz-link">Take the quiz</a>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-ink p-8 sm:p-10 space-y-5" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Name</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")}
                  className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors" placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Email</label>
                <input id="contact-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")}
                  className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors" placeholder="you@email.com" />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Message</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")}
                  className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors resize-none" placeholder="What's on your mind?" />
              </div>
              {error && <p data-testid="contact-error" className="text-red-400 text-xs">{error}</p>}
              <button type="submit" disabled={status === "loading"} data-testid="contact-submit-button"
                className="w-full bg-volt text-obsidian font-display font-bold px-6 py-4 hover:bg-white transition-colors disabled:opacity-50">
                {status === "loading" ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </main>
  );
}
